import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CategoryNode, LocationNode } from '../lib/api';
import { useCategories, useLocations } from '../lib/queries';
import { Icon } from './Icon';
import { ErrorState, Sheet, Spinner } from './ui';

function findPath<T extends { id: string; children: T[] }>(nodes: T[], id: string | null | undefined, path: T[] = []): T[] | null {
  if (!id) return null;
  for (const n of nodes) {
    if (n.id === id) return [...path, n];
    const r = findPath(n.children, id, [...path, n]);
    if (r) return r;
  }
  return null;
}

export function useCategoryPath(id: string | null | undefined) {
  const { data } = useCategories();
  return useMemo(() => (data ? (findPath(data, id) ?? []) : []), [data, id]);
}

export function useLocationPath(id: string | null | undefined) {
  const { data } = useLocations();
  return useMemo(() => (data ? (findPath(data, id) ?? []) : []), [data, id]);
}

/** Сетка корневых категорий с иконками. */
/** Мягкие цвета иконок категорий: так категории различаются с первого взгляда (в светлой и тёмной теме). */
export const CATEGORY_TONES = ['#2fa77f', '#3b82f6', '#f08c3a', '#8b5cf6', '#e0598b', '#14a3b8', '#d9a21b', '#e5534b'];

/** Сетка категорий на главной: цветные иконки, названия целыми словами (без переносов по слогам). */
export function CategoryGrid({ onSelect }: { onSelect: (c: CategoryNode) => void }) {
  const { data, isLoading, error, refetch } = useCategories();
  if (isLoading) return <div className="cat-grid">{Array.from({ length: 8 }, (_, i) => <div key={i} className="cat-item skeleton" />)}</div>;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return (
    <div className="cat-grid">
      {(data ?? []).map((c, i) => (
        <button key={c.id} type="button" className="cat-item" onClick={() => onSelect(c)} style={{ '--tone': CATEGORY_TONES[i % CATEGORY_TONES.length] } as React.CSSProperties}>
          <span className="cat-icon">
            <Icon name={c.icon ?? 'grid'} size={24} />
          </span>
          <span className="cat-name">{c.name}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Пошаговый выбор в дереве (категория → подкатегория, регион → район → пункт).
 * leafOnly: выбрать можно только конечный элемент (для публикации).
 */
function TreeSheet<T extends { id: string; name: string; children: T[] }>({
  open,
  onClose,
  title,
  roots,
  selectedId,
  onSelect,
  leafOnly,
  allowParentLabel,
  isSelectable,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  roots: T[];
  selectedId?: string | null;
  onSelect: (node: T | null) => void;
  leafOnly?: boolean;
  allowParentLabel?: string;
  isSelectable?: (n: T) => boolean;
}) {
  const { t } = useTranslation();
  const [stack, setStack] = useState<T[]>([]);
  const current = stack.length ? stack[stack.length - 1]!.children : roots;
  const parent = stack[stack.length - 1];
  const close = () => {
    setStack([]);
    onClose();
  };
  return (
    <Sheet open={open} onClose={close} title={parent ? parent.name : title}>
      {stack.length ? (
        <button type="button" className="list-row" onClick={() => setStack(stack.slice(0, -1))}>
          <Icon name="back" />
          <span className="list-row-label">{t('common.back')}</span>
        </button>
      ) : null}
      {!leafOnly && parent && allowParentLabel && (!isSelectable || isSelectable(parent)) ? (
        <button
          type="button"
          className="list-row list-row-strong"
          onClick={() => {
            onSelect(parent);
            close();
          }}
        >
          <span className="list-row-label">{allowParentLabel}</span>
          {selectedId === parent.id ? <Icon name="check" /> : null}
        </button>
      ) : null}
      {current.map((n) => {
        const hasChildren = n.children.length > 0;
        const selectable = !isSelectable || isSelectable(n);
        return (
          <button
            key={n.id}
            type="button"
            className="list-row"
            disabled={!hasChildren && !selectable}
            onClick={() => {
              if (hasChildren) setStack([...stack, n]);
              else {
                onSelect(n);
                close();
              }
            }}
          >
            <span className="list-row-label">{n.name}</span>
            {selectedId === n.id ? <Icon name="check" /> : null}
            {hasChildren ? <Icon name="chevron" size={18} className="muted" /> : null}
          </button>
        );
      })}
    </Sheet>
  );
}

export function CategoryPickerSheet(props: {
  open: boolean;
  onClose: () => void;
  selectedId?: string | null;
  onSelect: (c: CategoryNode | null) => void;
  leafOnly?: boolean;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useCategories();
  if (props.open && isLoading) return <Sheet open onClose={props.onClose} title={t('category.choose')}><Spinner /></Sheet>;
  return (
    <TreeSheet
      {...props}
      title={t('category.choose')}
      roots={data ?? []}
      allowParentLabel={props.leafOnly ? undefined : t('category.allIn')}
    />
  );
}

const LISTABLE = new Set(['district', 'city', 'settlement']);

/**
 * Выбор местоположения. Для фильтров можно выбрать любой уровень («весь район»);
 * для публикации — район, город или населённый пункт.
 */
export function LocationPickerSheet(props: {
  open: boolean;
  onClose: () => void;
  selectedId?: string | null;
  onSelect: (l: LocationNode | null) => void;
  forListing?: boolean;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useLocations();
  // Пропускаем уровень «страна», если она одна.
  const roots = useMemo(() => {
    let r = data ?? [];
    while (r.length === 1 && r[0]!.type === 'country') r = r[0]!.children;
    return r;
  }, [data]);
  if (props.open && isLoading) return <Sheet open onClose={props.onClose} title={t('location.choose')}><Spinner /></Sheet>;
  return (
    <TreeSheet
      {...props}
      title={t('location.choose')}
      roots={roots}
      allowParentLabel={props.forListing ? t('location.thisPlace') : t('location.allIn')}
      isSelectable={props.forListing ? (n) => LISTABLE.has(n.type) : undefined}
    />
  );
}

/** Цвет и значок корневой категории (для фирменной заглушки объявления без фото). */
export function useCategoryLook(categoryId: string | null | undefined): { icon: string; tone: string; name: string } | null {
  const { data } = useCategories();
  return useMemo(() => {
    if (!data || !categoryId) return null;
    for (let i = 0; i < data.length; i++) {
      const root = data[i]!;
      if (findPath([root], categoryId)) return { icon: root.icon ?? 'grid', tone: CATEGORY_TONES[i % CATEGORY_TONES.length]!, name: root.name };
    }
    return null;
  }, [data, categoryId]);
}
